import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import React from 'react';
import styled from 'styled-components';

import { IconInvalid } from '../../easyclaim.app.assets/index';

interface ModalProps {
	isOpen: boolean;
	closeOnBackdropClick?: boolean;
	header?: React.ReactNode;
	children?: React.ReactChild;
	footer?: React.ReactNode;
	onClose: () => void;
	doNotShowClose?: boolean;
	className?: string;
}

interface ModalState {
	isClosing: boolean;
}

export class Modal extends React.Component<ModalProps, ModalState> {
	body?: HTMLDivElement | null;

	constructor(props: ModalProps) {
		super(props);
		this.state = {
			isClosing: false,
		};
	}

	UNSAFE_componentWillReceiveProps(newProps: ModalProps) {
		if (!this.props.isOpen && newProps.isOpen && this.body) {
			disableBodyScroll(this.body);
			this.setState({ isClosing: false });
		} else if (this.props.isOpen && !newProps.isOpen && this.body) {
			enableBodyScroll(this.body);
		}
	}

	componentWillUnmount() {
		clearAllBodyScrollLocks();
	}

	handleClose = () => {
		this.setState({ isClosing: true });
	};

	onAnimationEnd = (e: React.AnimationEvent) => {
		if (e.animationName === 'slideDown' && this.state.isClosing && e.target === e.currentTarget) {
			this.props.onClose();
			this.setState({ isClosing: false });
		}
	};

	onBackdropClicked = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		e.stopPropagation();
		if (this.props.closeOnBackdropClick) {
			this.handleClose();
		}
	};

	render() {
		const { isOpen } = this.props;
		const { isClosing } = this.state;

		const shouldShow = isOpen || isClosing;
		const modalClass = isOpen && !isClosing ? 'modal-container-open' : 'modal-container-closed';

		return (
			<>
				<ModalContainer
					className={`modal-container ${this.props.className} ${modalClass}`}
					isOpen={shouldShow}
					onAnimationEnd={this.onAnimationEnd}
				>
					<ContentContainer className="content-container" isOpen={shouldShow}>
						{!this.props.doNotShowClose ? (
							<StyledIconTime
								className="button-close-InvalidIcon"
								onClick={(e) => {
									e.stopPropagation();
									this.handleClose();
								}}
							/>
						) : null}
						{this.props.header && <Header className="modal-header">{this.props.header}</Header>}
						{this.props.children && (
							<Body className="modal-body" ref={(r) => (this.body = r)}>
								{this.props.children}
							</Body>
						)}
						{this.props.footer && <Footer className="modal-footer">{this.props.footer}</Footer>}
					</ContentContainer>
				</ModalContainer>
				<Backdrop isOpen={isOpen} onClick={this.onBackdropClicked} />
			</>
		);
	}
}

const ModalContainer = styled.div<{ isOpen: boolean }>`
	visibility: ${(p) => (p.isOpen ? 'visible' : 'hidden')};
`;

const ContentContainer = styled.div<{ isOpen: boolean }>`
	padding: 2rem 0rem;
	max-height: 80vh;
	position: relative;
	border-radius: 3px;
	margin: 0 auto;
	transform: ${(p) => (p.isOpen ? 'scale(1)' : 'scale(0.7)')};
	opacity: ${(p) => (p.isOpen ? '1' : '0')};
	transition: all 0.3s;
	display: flex;
	flex-direction: column;
`;

const StyledIconTime = styled(IconInvalid)`
	color: ${(props) => props.theme.colors.secondary};
	height: 2rem;
	position: absolute;
	top: 0rem;
	right: 0rem;
	padding: 1rem;
	transition:
		color 0.1s,
		transform 0.1s;
	-webkit-tap-highlight-color: rgba(0, 0, 0, 0);
	&:hover {
		color: lighten(0.1, ${(p) => p.theme.colors.primary});
	}
	&:active {
		color: lighten(0.1, ${(p) => p.theme.colors.primary});
		transform: scale(0.8);
	}
`;

const Footer = styled.div`
	margin: 0 auto;
	padding: 1rem 1rem 0.5rem 1rem;
	flex-shrink: 0;
	flex-grow: 0;
`;

const Header = styled.div`
	text-align: left;
	padding: 0rem 4rem 2rem 2rem;
	flex-shrink: 0;
	flex-grow: 0;
	@media only screen and (max-width: 400px) {
		padding: 0rem 4rem 2rem 1rem;
	}
`;

const Body = styled.div`
	padding: 0rem 2rem 0rem 2rem;
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	flex-shrink: 1;
	flex-grow: 1;
	@media only screen and (max-width: 400px) {
		padding: 0rem 1rem 0rem 1rem;
	}
`;

const Backdrop = styled.div<{ isOpen: boolean }>`
	position: fixed;
	width: 100%;
	height: 100%;
	visibility: ${(p) => (p.isOpen ? 'visible' : 'hidden')};
	top: 0;
	left: 0;
	z-index: 1200;
	opacity: ${(p) => (p.isOpen ? '1' : '0')};
	background: rgba(0, 0, 0, 0.4);
	transition: all 0.3s;
	overflow: hidden;
	touch-action: none;
`;
